/**
 * The readiness checkpoint of a setup revision.
 *
 * A revision whose profile was saved and read back still has one thing left to
 * prove: that the setup it recorded is mappable against the model. This hook
 * reports the mapping readiness verdict into the revision machine, which is what
 * finally settles the revision - or sends it back to its profile checkpoint so
 * the user can fix the mapping and save again.
 */

import { useEffect } from 'react';
import { resolveSetupReadinessAction } from '../../../shared/lib/setupRevision';
import { useSetupRevisionStatus, useUIStore } from '../../../shared/stores/useUIStore';
import { resolveMappingReadinessVerdict } from '../lib/readiness/readinessVerdict';
import type { MappingProjectReadinessStatus } from '../lib/readiness/readinessResult';

export function useSetupReadinessCheckpoint(readinessStatus: MappingProjectReadinessStatus): void {
  const setupRevisionStatus = useSetupRevisionStatus();
  const confirmSetupReadiness = useUIStore((state) => state.confirmSetupReadiness);
  const applySetupRevisionEvent = useUIStore((state) => state.applySetupRevisionEvent);

  useEffect(() => {
    const action = resolveSetupReadinessAction(
      setupRevisionStatus,
      resolveMappingReadinessVerdict(readinessStatus)
    );

    if (action === 'confirm') {
      confirmSetupReadiness();
      return;
    }

    if (action === 'reportFailure') {
      applySetupRevisionEvent('readinessFailed');
    }
  }, [applySetupRevisionEvent, confirmSetupReadiness, readinessStatus, setupRevisionStatus]);
}
